// กล่องรับเพชร (นับถอยหลัง) + ประวัติแต้มเพชร — logic ร่วมของ landing / landing-ver2 (หน้าตาแยกกันที่ DiamondBox.js)
// ค่าเพชรต่อกล่อง/เวลานับถอยหลังมาจากที่แอดมินตั้งในหน้า /wheeling → "ตั้งค่ากล่องรับเพชร" (GET /countdownbox/status/:id)
// หมายเหตุชื่อไฟล์: ห้ามตั้งให้ต่างจาก DiamondBox.js แค่ตัวพิมพ์เล็ก/ใหญ่ — macOS มองเป็นไฟล์เดียวกันและเขียนทับกัน
import { useCallback, useEffect, useRef, useState } from "react";
import lottoApi from "./api";
import { pushToast, useInterval } from "./lib";

export const DIAMOND_CHANGED = "lx:diamond-changed"; // ยิงหลังรับเพชร — หน้ากงล้อฟังเพื่อรีโหลดแต้ม
export const DIAMOND_OPEN = "lx:diamond-open"; // detail: { tab: "box" | "history" } — ปุ่มที่ไหนก็เปิดกล่องได้

// แหล่งที่มาของเพชร (source ใน diamond_log) · tone ใช้ในดีไซน์ที่ต้องการสีประจำแหล่ง
export const DIAMOND_SOURCES = {
  OPENBOX: { label: "กล่องรับเพชร", icon: "fluent-emoji-flat:wrapped-gift", tone: "#f59e0b" },
  TOPUP: { label: "ฝากเงิน", icon: "fluent-emoji-flat:money-bag", tone: "#22c55e" },
  CREDITCODE: { label: "โค้ดรางวัล", icon: "fluent-emoji-flat:ticket", tone: "#38bdf8" },
  ACTIVITY: { label: "กิจกรรม", icon: "fluent-emoji-flat:party-popper", tone: "#f472b6" },
  CHECKIN: { label: "เช็คอินรายวัน", icon: "fluent-emoji-flat:calendar", tone: "#a78bfa" },
  ADMIN: { label: "แอดมินเพิ่มให้", icon: "fluent-emoji-flat:crown", tone: "#fbbf24" },
};
const OTHER_SOURCE = { label: "อื่นๆ", icon: "fluent-emoji-flat:gem-stone", tone: "#94a3b8" };
export const sourceMeta = (source) => DIAMOND_SOURCES[String(source || "").toUpperCase()] || OTHER_SOURCE;

const p2 = (n) => String(n).padStart(2, "0");

export function splitHMS(ms) {
  const t = Math.max(0, Math.ceil(ms / 1000));
  return { h: Math.floor(t / 3600), m: Math.floor((t % 3600) / 60), s: t % 60 };
}

export function fmtHMS(ms) {
  const { h, m, s } = splitHMS(ms);
  return `${p2(h)}:${p2(m)}:${p2(s)}`;
}

// "3 ชั่วโมง" / "90 นาที" / "1 ชั่วโมง 30 นาที" — ใช้บอกรอบเวลาที่แอดมินตั้ง
export function describeInterval(minutes) {
  const total = Math.round(Number(minutes) || 0);
  if (total <= 0) return "ไม่มีเวลารอ";
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h && m) return `${h} ชั่วโมง ${m} นาที`;
  if (h) return `${h} ชั่วโมง`;
  return `${m} นาที`;
}

const TH_MONTH = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export const fmtClockTime = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : `${p2(d.getHours())}:${p2(d.getMinutes())}`;
};

// หัวข้อวัน: วันนี้ / เมื่อวาน / 29 ต.ค.
export function dayLabel(iso, now = new Date()) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  if (sameDay(d, now)) return "วันนี้";
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (sameDay(d, y)) return "เมื่อวาน";
  return `${d.getDate()} ${TH_MONTH[d.getMonth()]}${d.getFullYear() !== now.getFullYear() ? ` ${d.getFullYear() + 543}` : ""}`;
}

export const fmtWhen = (iso, now = new Date()) => `${dayLabel(iso, now)} ${fmtClockTime(iso)}`.trim();

export function groupByDay(items, now = new Date()) {
  const groups = [];
  items.forEach((it) => {
    const label = dayLabel(it.created_at, now);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(it);
    else groups.push({ label, items: [it] });
  });
  return groups;
}

/** แอดมินเปิด/ปิดกล่องที่ /lotto/site-features (features.diamond_box) — ไม่มีค่า/API ล่ม = ถือว่าเปิด */
export function useDiamondBoxEnabled() {
  const [on, setOn] = useState(true);
  useEffect(() => {
    let alive = true;
    lottoApi
      .features()
      .catch(() => null)
      .then((f) => alive && setOn(!f || f.diamond_box !== false));
    return () => {
      alive = false;
    };
  }, []);
  return on;
}

/** สถานะกล่อง + นับถอยหลังตามเวลาเซิร์ฟเวอร์ (ชดเชยนาฬิกาเครื่องที่เดินไม่ตรง) */
export function useDiamondBox(uid) {
  const [status, setStatus] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const offset = useRef(0);

  const load = useCallback(async () => {
    if (!uid) {
      // ผู้เยี่ยมชม: โชว์ค่าที่แอดมินตั้งอย่างเดียว (ไม่มีนับถอยหลัง/กดรับไม่ได้) — ชวนเข้าสู่ระบบ
      try {
        const s = await lottoApi.countdownBoxSetting();
        const minutes = Number(s?.countdown_time) || 0;
        setStatus(s ? { point_value: s.point_value, countdown_minutes: minutes, countdown_seconds: Math.round(minutes * 60), countdown_end: null, server_now: null } : null);
      } catch {
        setStatus(null);
      }
      return null;
    }
    try {
      const s = await lottoApi.countdownBoxStatus(uid);
      if (s?.server_now) offset.current = new Date(s.server_now).getTime() - Date.now();
      setNow(Date.now());
      setStatus(s);
      return s;
    } catch {
      return null;
    }
  }, [uid]);

  useEffect(() => {
    load();
  }, [load]);

  // กลับมาที่แท็บ/หน้าจอ → ซิงก์ใหม่ (แท็บที่หลับอยู่ timer จะเพี้ยน)
  useEffect(() => {
    const onVis = () => document.visibilityState === "visible" && load();
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [load]);

  useInterval(() => setNow(Date.now()), status && uid ? 1000 : null);

  const endMs = status?.countdown_end ? new Date(status.countdown_end).getTime() : null;
  const remaining = endMs ? Math.max(0, endMs - (now + offset.current)) : 0;
  const total = (Number(status?.countdown_seconds) || 0) * 1000;
  const guest = !uid;
  const ready = !guest && !!status && remaining <= 0;
  const progress = ready ? 1 : total > 0 ? Math.min(1, Math.max(0, 1 - remaining / total)) : 0;

  const claim = useCallback(async () => {
    if (!uid || !ready || claiming) return false;
    setClaiming(true);
    try {
      const r = await lottoApi.countdownBoxReceive(uid);
      if (r?.message === "Diamond Point Added Success") {
        pushToast(`รับเพชร +${status?.point_value ?? ""} เรียบร้อย`, "ok");
        window.dispatchEvent(new CustomEvent(DIAMOND_CHANGED));
        await load();
        return true;
      }
      const known = {
        "Countdown not finished yet": "ยังไม่ครบเวลานับถอยหลัง",
        "Diamond box disabled": "ระบบกล่องรับเพชรปิดให้บริการชั่วคราว",
      };
      pushToast(known[r?.message] || r?.message || "รับเพชรไม่สำเร็จ", "err");
      await load();
    } catch (e) {
      const msg = String(e?.response?.data?.message || e?.message || "");
      pushToast(/TWICE/i.test(msg) ? "กดรับถี่เกินไป กรุณารอสักครู่" : "รับเพชรไม่สำเร็จ ลองใหม่อีกครั้ง", "err");
      await load();
    } finally {
      setClaiming(false);
    }
    return false;
  }, [uid, ready, claiming, status, load]);

  return {
    status,
    loaded: !!status,
    guest,
    ready,
    remaining,
    total,
    progress,
    claiming,
    claim,
    reload: load,
    pointValue: Number(status?.point_value) || 0,
    minutes: Number(status?.countdown_minutes) || 0,
  };
}

/** ประวัติเพชรที่ได้รับ (เลื่อนโหลดเพิ่มด้วย before=id สุดท้าย) */
export function useDiamondHistory(uid, enabled = true, pageSize = 20) {
  const [state, setState] = useState({ items: [], hasMore: false, summary: null, loading: false, loaded: false });

  const fetchPage = useCallback(
    async (before) => {
      const r = await lottoApi.diamondHistory(uid, { limit: pageSize, ...(before ? { before } : {}) });
      return { items: r?.data || [], hasMore: !!r?.hasMore, summary: r?.summary || null };
    },
    [uid, pageSize],
  );

  const reload = useCallback(async () => {
    if (!uid) return;
    setState((s) => ({ ...s, loading: true }));
    try {
      const p = await fetchPage();
      setState({ ...p, loading: false, loaded: true });
    } catch {
      setState((s) => ({ ...s, loading: false, loaded: true }));
    }
  }, [uid, fetchPage]);

  const loadMore = useCallback(async () => {
    const last = state.items[state.items.length - 1];
    if (!last || state.loading) return;
    setState((s) => ({ ...s, loading: true }));
    try {
      const p = await fetchPage(last.id);
      setState((s) => ({ items: [...s.items, ...p.items], hasMore: p.hasMore, summary: p.summary || s.summary, loading: false, loaded: true }));
    } catch {
      setState((s) => ({ ...s, loading: false }));
    }
  }, [state.items, state.loading, fetchPage]);

  useEffect(() => {
    if (enabled) reload();
  }, [enabled, reload]);

  useEffect(() => {
    if (!enabled) return undefined;
    window.addEventListener(DIAMOND_CHANGED, reload);
    return () => window.removeEventListener(DIAMOND_CHANGED, reload);
  }, [enabled, reload]);

  return { ...state, reload, loadMore };
}
